import { create } from 'zustand';

/**
 * editMode — Modo Edición del dashboard (solo admin).
 *
 * Con `active` en true, hacer click en un regulador abre `RegulatorEditPanel`
 * en vez de `ControlPanel` (ver `SantaColomaMapView.tsx`). Modos de
 * "picking" mutuamente excluyentes, uno a la vez:
 *   - `picking` — al pulsar "Reposicionar": el siguiente click en el mapa se
 *     interpreta como la nueva posición candidata (`pendingCoord`).
 *   - `pickingSignal` — al pulsar "Asignar semáforo": el siguiente click
 *     sobre un `TrafficSignalDot` se interpreta como el semáforo candidato
 *     (`pendingSignalId`) a cablear a un grupo del regulador seleccionado.
 *   - `addingRegulator` — al pulsar "+ Regulador" (ARS-05): el siguiente
 *     click en el mapa fija la posición de un regulador nuevo
 *     (`pendingNewRegulatorCoord`), sin reposicionar nada existente.
 *   - `addingSignal` — al pulsar "+ Semáforo" (ARS-05): igual, para un
 *     semáforo nuevo (`pendingNewSignalCoord`).
 */
interface EditModeState {
  active: boolean;
  toggle: () => void;

  picking: boolean;
  pendingCoord: [number, number] | null;
  startPicking: () => void;
  stopPicking: () => void;
  setPendingCoord: (coord: [number, number]) => void;
  clearPendingCoord: () => void;

  pickingSignal: boolean;
  pendingSignalId: number | null;
  startPickingSignal: () => void;
  stopPickingSignal: () => void;
  setPendingSignalId: (id: number) => void;
  clearPendingSignalId: () => void;

  addingRegulator: boolean;
  pendingNewRegulatorCoord: [number, number] | null;
  startAddingRegulator: () => void;
  stopAddingRegulator: () => void;
  setPendingNewRegulatorCoord: (coord: [number, number]) => void;
  clearPendingNewRegulatorCoord: () => void;

  addingSignal: boolean;
  pendingNewSignalCoord: [number, number] | null;
  startAddingSignal: () => void;
  stopAddingSignal: () => void;
  setPendingNewSignalCoord: (coord: [number, number]) => void;
  clearPendingNewSignalCoord: () => void;
}

// Estado inicial compartido de los cuatro modos de picking — cancela
// cualquiera que estuviera activo al arrancar otro, o al salir de edición.
const PICKING_RESET = {
  picking: false, pendingCoord: null,
  pickingSignal: false, pendingSignalId: null,
  addingRegulator: false, pendingNewRegulatorCoord: null,
  addingSignal: false, pendingNewSignalCoord: null,
};

export const useEditMode = create<EditModeState>((set) => ({
  active: false,
  toggle: () => set((s) => ({ active: !s.active, ...PICKING_RESET })),

  picking: false,
  pendingCoord: null,
  startPicking: () => set({ ...PICKING_RESET, picking: true }),
  stopPicking: () => set({ picking: false }),
  setPendingCoord: (coord) => set({ pendingCoord: coord }),
  clearPendingCoord: () => set({ pendingCoord: null }),

  pickingSignal: false,
  pendingSignalId: null,
  startPickingSignal: () => set({ ...PICKING_RESET, pickingSignal: true }),
  stopPickingSignal: () => set({ pickingSignal: false }),
  setPendingSignalId: (id) => set({ pendingSignalId: id }),
  clearPendingSignalId: () => set({ pendingSignalId: null }),

  addingRegulator: false,
  pendingNewRegulatorCoord: null,
  startAddingRegulator: () => set({ ...PICKING_RESET, addingRegulator: true }),
  stopAddingRegulator: () => set({ addingRegulator: false }),
  setPendingNewRegulatorCoord: (coord) => set({ pendingNewRegulatorCoord: coord }),
  clearPendingNewRegulatorCoord: () => set({ pendingNewRegulatorCoord: null }),

  addingSignal: false,
  pendingNewSignalCoord: null,
  startAddingSignal: () => set({ ...PICKING_RESET, addingSignal: true }),
  stopAddingSignal: () => set({ addingSignal: false }),
  setPendingNewSignalCoord: (coord) => set({ pendingNewSignalCoord: coord }),
  clearPendingNewSignalCoord: () => set({ pendingNewSignalCoord: null }),
}));
